import { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';
import { observer } from 'mobx-react';
import messages from 'messages';
import {
  ActionButton,
  Panel,
  PanelBody,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper,
  RefactoredPanelHeader
} from 'components/panel';
import { ROUTE_EXERCISES_TRAININGS_ENTITY } from 'routes/RouteList';
import { MenuWrapper } from 'components/menu';
import { StoreContext } from 'index';
import Loader from 'components/loader/Loader';
import BreadcrumbPath from 'containers/partials/misc/BreadcrumbPath';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import { TabList, Tab, TabPanel, Tabs, TabPanelBody } from 'components/tabs';
import AddTrainingModal from 'containers/pages/exercises/trainings/AddTrainingModal';
import useTabIndex from 'hooks/utils/useTabIndex';
import { Link } from 'react-router-dom';
import Training from 'models/Training';
import useTrainings from 'hooks/queries/useTrainings';
import { TemplateType } from 'constants.js';
import AddButton from 'containers/partials/buttons/AddButton';
import TrainingsTab from 'containers/pages/exercises/trainings/TrainingsTab';

const Trainings = () => {
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const { entityId } = useParams();
  const ability = useAbility(AbilityContext);
  const { fetchTrainings, loading } = useTrainings(entityId, user);

  const [addModalData, setAddModalData] = useState(null);

  const [tabIndex, setTabIndex] = useTabIndex([
    ROUTE_EXERCISES_TRAININGS_ENTITY
  ]);
  const active = useRouteMatch([ROUTE_EXERCISES_TRAININGS_ENTITY]);

  useEffect(() => {
    const getTrainings = async () => {
      await fetchTrainings(
        { variables: { entityId, type: TemplateType.TRAINING } },
        user
      );
    };

    getTrainings();
  }, []);

  return (
    <Panel active={active.isExact}>
      <RefactoredPanelHeader>
        <PanelTitleWrapper>
          <BreadcrumbPath crumbs={[]} />
          <PanelTitle>
            <FormattedMessage {...messages.trainingsTitle} />
          </PanelTitle>
          <PanelSubtitle>
            <FormattedMessage {...messages.trainingsSubTitle} />
          </PanelSubtitle>
        </PanelTitleWrapper>
        <PanelTitleButtonsGroup>
          {ability.can('create', 'Training') && (
            <ActionButton>
              <AddButton
                expandable
                label={
                  <FormattedMessage {...messages.exercisesAddTrainingButton} />
                }
                onClick={() => setAddModalData(true)}
              ></AddButton>
            </ActionButton>
          )}
          <MenuWrapper trigger={<PanelTitleButton />} />
        </PanelTitleButtonsGroup>
      </RefactoredPanelHeader>
      <PanelBody>
        {loading && <Loader />}
        <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
          <TabList>
            <Tab key={`trainings.my`}>
              <Link
                to={generatePath(ROUTE_EXERCISES_TRAININGS_ENTITY, {
                  entityId
                })}
              >
                <FormattedMessage {...messages.trainingsTabTrainings} />
              </Link>
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelBody withScroller withHeader>
              <TrainingsTab />
            </TabPanelBody>
          </TabPanel>
        </Tabs>
      </PanelBody>
      {addModalData && (
        <AddTrainingModal
          entityId={entityId}
          training={new Training({})}
          onClose={() => setAddModalData(false)}
        />
      )}
    </Panel>
  );
};

export default observer(Trainings);
